import React, { useState, useEffect } from "react";
import useSWR from "swr";
import {
  Table,
  Drawer,
  Descriptions,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getCommunityReports,
  getCommunityReportDetail,
  updateCommunityReportStatus,
  deleteCommunityReport,
  lockCommunityPost,
  unlockCommunityPost,
  forceDeleteCommunityPost,
  forceDeleteCommunityComment,
} from "../api/domainApi";
import {
  CmntyReportListItem,
  ReportStatus,
  ReportTargetType,
  ReportReasonCode,
} from "../types/domain";
import PageHeader from "../components/common/PageHeader";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<ReportStatus, string> = {
  PENDING: "대기",
  REVIEWED: "검토완료",
  REJECTED: "반려",
  ACTIONED: "조치완료",
};
const STATUS_COLOR: Record<ReportStatus, string> = {
  PENDING: "orange",
  REVIEWED: "blue",
  REJECTED: "default",
  ACTIONED: "green",
};
const REASON_LABEL: Record<ReportReasonCode, string> = {
  ABUSE: "욕설/비방",
  SPAM: "스팸/광고",
  HARASS: "괴롭힘",
  INFO_FALSE: "허위정보",
  ETC: "기타",
};
const TARGET_LABEL: Record<ReportTargetType, string> = {
  POST: "게시글",
  COMMENT: "댓글",
};

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as ReportStatus[]).map(
  (s) => ({
    value: s,
    label: STATUS_LABEL[s],
  }),
);
const REASON_OPTIONS = (Object.keys(REASON_LABEL) as ReportReasonCode[]).map(
  (r) => ({
    value: r,
    label: REASON_LABEL[r],
  }),
);

const CommunityReportPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ReportStatus | undefined>();
  const [targetType, setTargetType] = useState<ReportTargetType | undefined>();
  const [reasonCode, setReasonCode] = useState<ReportReasonCode | undefined>();
  const [sort, setSort] = useState<"pending" | "latest">("pending");
  const [reporterUserId, setReporterUserId] = useState<number | undefined>();
  const [postId, setPostId] = useState<number | undefined>();
  const [reportedUserId, setReportedUserId] = useState<number | undefined>();

  const [detailId, setDetailId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 상태변경 폼
  const [newStatus, setNewStatus] = useState<ReportStatus | undefined>();
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);

  const swrKey = `/community/reports?p=${page}&s=${status}&t=${targetType}&r=${reasonCode}&sort=${sort}&ru=${reporterUserId}&pi=${postId}&tu=${reportedUserId}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getCommunityReports({
        page: page - 1,
        size: PAGE_SIZE,
        sort,
        status,
        targetType,
        reasonCode,
        reporterUserId,
        postId,
        reportedUserId,
      }),
    { keepPreviousData: true },
  );

  const { data: detail, mutate: mutateDetail } = useSWR(
    detailId ? `/community/reports/${detailId}` : null,
    () => getCommunityReportDetail(detailId!),
  );

  useEffect(() => {
    if (detail) {
      setNewStatus(detail.status);
      setMemo(detail.reviewMemo ?? "");
    }
  }, [detail]);

  const openDetail = (row: CmntyReportListItem) => {
    setDetailId(row.reportId);
    setDrawerOpen(true);
  };

  const refresh = () => {
    mutate();
    mutateDetail();
  };

  const handleSaveStatus = async () => {
    if (!detail || !newStatus) return;
    setSaving(true);
    try {
      await updateCommunityReportStatus(detail.reportId, {
        status: newStatus,
        reviewMemo: memo,
      });
      message.success("처리 상태가 변경되었습니다.");
      refresh();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      refresh();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<CmntyReportListItem> = [
    { title: "ID", dataIndex: "reportId", width: 70 },
    {
      title: "대상",
      dataIndex: "targetType",
      width: 90,
      render: (v: ReportTargetType, r) => `${TARGET_LABEL[v]} #${r.targetId}`,
    },
    {
      title: "사유",
      dataIndex: "reasonCode",
      width: 100,
      render: (v: ReportReasonCode) => REASON_LABEL[v] ?? v,
    },
    {
      title: "사유 상세",
      dataIndex: "reasonDetail",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "신고자",
      dataIndex: "reporterUserId",
      width: 140,
      render: (v: number, r) =>
        r.reporterNickname ? `${r.reporterNickname} (#${v})` : `#${v}`,
    },
    {
      title: "상태",
      dataIndex: "status",
      width: 100,
      render: (v: ReportStatus) => (
        <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>
      ),
    },
    {
      title: "접수일시",
      dataIndex: "createdAt",
      width: 160,
      render: (v: string) => (v ? v.replace("T", " ").slice(0, 16) : "-"),
    },
    {
      title: "관리",
      width: 90,
      render: (_, row) => (
        <Button size="small" type="dashed" onClick={() => openDetail(row)}>
          상세
        </Button>
      ),
    },
  ];

  const reset = () => {
    setStatus(undefined);
    setTargetType(undefined);
    setReasonCode(undefined);
    setSort("pending");
    setReporterUserId(undefined);
    setPostId(undefined);
    setReportedUserId(undefined);
    setPage(1);
  };

  return (
    <div>
      <PageHeader title="커뮤니티 신고 관리" />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="상태 전체"
          allowClear
          style={{ width: 130 }}
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        />
        <Select
          placeholder="대상 전체"
          allowClear
          style={{ width: 120 }}
          options={[
            { value: "POST", label: "게시글" },
            { value: "COMMENT", label: "댓글" },
          ]}
          value={targetType}
          onChange={(v) => {
            setTargetType(v);
            setPage(1);
          }}
        />
        <Select
          placeholder="사유 전체"
          allowClear
          style={{ width: 130 }}
          options={REASON_OPTIONS}
          value={reasonCode}
          onChange={(v) => {
            setReasonCode(v);
            setPage(1);
          }}
        />
        <Select
          style={{ width: 130 }}
          options={[
            { value: "pending", label: "미처리 우선" },
            { value: "latest", label: "최신순" },
          ]}
          value={sort}
          onChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        />
        <InputNumber
          placeholder="신고자 ID"
          style={{ width: 120 }}
          controls={false}
          value={reporterUserId}
          onChange={(v) => {
            setReporterUserId(v ?? undefined);
            setPage(1);
          }}
        />
        <InputNumber
          placeholder="게시글 ID"
          style={{ width: 120 }}
          controls={false}
          value={postId}
          onChange={(v) => {
            setPostId(v ?? undefined);
            setPage(1);
          }}
        />
        <InputNumber
          placeholder="신고대상자 ID"
          style={{ width: 130 }}
          controls={false}
          value={reportedUserId}
          onChange={(v) => {
            setReportedUserId(v ?? undefined);
            setPage(1);
          }}
        />
        <Button onClick={reset}>초기화</Button>
      </Space>
      <Table
        rowKey="reportId"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      <Drawer
        title={`신고 상세 #${detail?.reportId ?? ""}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={680}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="대상">
                {TARGET_LABEL[detail.targetType]} #{detail.targetId}
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                <Tag color={STATUS_COLOR[detail.status]}>
                  {STATUS_LABEL[detail.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="사유">
                {REASON_LABEL[detail.reasonCode] ?? detail.reasonCode}
              </Descriptions.Item>
              <Descriptions.Item label="신고자">
                {detail.reporterNickname
                  ? `${detail.reporterNickname} (#${detail.reporterUserId})`
                  : `#${detail.reporterUserId}`}
              </Descriptions.Item>
              <Descriptions.Item label="사유 상세" span={2}>
                {detail.reasonDetail || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="접수일시" span={2}>
                {detail.createdAt?.replace("T", " ").slice(0, 19)}
              </Descriptions.Item>
              <Descriptions.Item label="처리자">
                {detail.reviewedId || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="처리일시">
                {detail.reviewedAt
                  ? detail.reviewedAt.replace("T", " ").slice(0, 19)
                  : "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider titlePlacement="left">신고된 본문</Divider>
            {detail.post && (
              <Descriptions
                bordered
                size="small"
                column={1}
                style={{ marginBottom: 12 }}
              >
                <Descriptions.Item label="게시판">
                  {detail.post.boardCode}
                </Descriptions.Item>
                <Descriptions.Item label="제목">
                  {detail.post.title}
                </Descriptions.Item>
                <Descriptions.Item label="내용">
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {detail.post.content}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="작성자">
                  {detail.post.authorUserId}
                </Descriptions.Item>
                <Descriptions.Item label="작성자">
                  {detail.post.authorNickname
                    ? `${detail.post.authorNickname} (#${detail.post.authorUserId})`
                    : `#${detail.post.authorUserId}`}
                </Descriptions.Item>
                <Descriptions.Item label="상태">
                  {detail.post.delYn ? (
                    <Tag color="red">삭제됨</Tag>
                  ) : (
                    <Tag color="green">노출중</Tag>
                  )}
                  {detail.post.adminLockYn && (
                    <Tag color="volcano">운영자 잠금</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
            {detail.comment && (
              <Descriptions
                bordered
                size="small"
                column={1}
                style={{ marginBottom: 12 }}
              >
                <Descriptions.Item label="댓글 ID">
                  #{detail.comment.commentId} (게시글 #{detail.comment.postId})
                </Descriptions.Item>
                <Descriptions.Item label="내용">
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {detail.comment.content}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="작성자">
                  {detail.comment.authorUserId}
                </Descriptions.Item>
                <Descriptions.Item label="작성자">
                  {detail.comment.authorNickname
                    ? `${detail.comment.authorNickname} (#${detail.comment.authorUserId})`
                    : `#${detail.comment.authorUserId}`}
                </Descriptions.Item>
                <Descriptions.Item label="상태">
                  {detail.comment.delYn ? (
                    <Tag color="red">삭제됨</Tag>
                  ) : (
                    <Tag color="green">노출중</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}

            <Divider titlePlacement="left">처리</Divider>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space>
                <span>상태</span>
                <Select
                  style={{ width: 150 }}
                  options={STATUS_OPTIONS}
                  value={newStatus}
                  onChange={setNewStatus}
                />
              </Space>
              <Input.TextArea
                rows={2}
                placeholder="처리 메모 (선택, 최대 500자)"
                maxLength={500}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
              <Button
                type="primary"
                loading={saving}
                onClick={handleSaveStatus}
              >
                상태 저장
              </Button>
            </Space>

            <Divider titlePlacement="left">조치 (대상 본문)</Divider>
            <Space wrap>
              {detail.post && (
                <>
                  {detail.post.adminLockYn ? (
                    <Button
                      onClick={() =>
                        act(
                          () => unlockCommunityPost(detail.post!.postId),
                          "게시글 잠금 해제됨",
                        )
                      }
                    >
                      게시글 잠금 해제
                    </Button>
                  ) : (
                    <Button
                      onClick={() =>
                        act(
                          () => lockCommunityPost(detail.post!.postId),
                          "게시글 잠금됨",
                        )
                      }
                    >
                      게시글 잠금
                    </Button>
                  )}
                  <Popconfirm
                    title="게시글을 강제 삭제하시겠습니까?"
                    onConfirm={() =>
                      act(
                        () => forceDeleteCommunityPost(detail.post!.postId),
                        "게시글 삭제됨",
                      )
                    }
                  >
                    <Button danger disabled={detail.post.delYn}>
                      게시글 강제삭제
                    </Button>
                  </Popconfirm>
                </>
              )}
              {detail.comment && (
                <Popconfirm
                  title="댓글을 강제 삭제하시겠습니까?"
                  onConfirm={() =>
                    act(
                      () =>
                        forceDeleteCommunityComment(detail.comment!.commentId),
                      "댓글 삭제됨",
                    )
                  }
                >
                  <Button danger disabled={detail.comment.delYn}>
                    댓글 강제삭제
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title="이 신고를 숨기시겠습니까?"
                onConfirm={() =>
                  act(
                    () => deleteCommunityReport(detail.reportId),
                    "신고 숨김 처리됨",
                  )
                }
              >
                <Button>신고 숨김</Button>
              </Popconfirm>
            </Space>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default CommunityReportPage;
